import { useCallback } from "react";
import { snakeCase } from "lodash-es";
import { cloudProviderIcons } from "./CloudProviderIcons/CloudProviderIconsMap";
import { dataSourceIcons, DataSourceIcon } from "./DatasourceIconsMap";
import { DefaultAppIcons } from "./SeemAppIcons";
type ThemeType = "LIGHT" | "DARK";

export const useIcon = (wantedTheme?: ThemeType) => {
  const currentTheme = wantedTheme || 
  (localStorage.getItem("current_theme_ls") === "theme-dark" ? "DARK" : "LIGHT");

  /**
   * Get the icon for the provider
   * @param provider - The provider name
   * @param ignoreUnknown - Whether to return the unknown icon if no icon is found
   * @returns An svg icon of the provider
   */
  const getProviderIcon = useCallback(
    (provider: string, ignoreUnknown = false): string | null => {
      const providerKey = snakeCase(provider?.toLowerCase());
      const icon = cloudProviderIcons[providerKey];
      if (!icon) {
        return !ignoreUnknown
          ? (cloudProviderIcons.unknown[
              (currentTheme as ThemeType) || "LIGHT"
            ] as string)
          : null;
      }

      return icon[currentTheme as ThemeType] || icon.LIGHT;
    },
    [currentTheme]
  );

  /**
   * Get the icon for the data source - WIP
   * @param dataSource - The datasource_definition.name value - this is what is used in the data source icons list
   * @param size - The size of the icon to get, default is null
   * @param ignoreUnknown - Whether to return the unknown icon if no icon is found
   * @returns If no specific size is requested, the full icon is returned. If a size is requested, the icon of the requested size is returned.
   */
  const getDataSourceIcon = useCallback(
    (
      dataSource: string,
      size: "S" | "L" | null = null,
      ignoreUnknown = false
    ): string | DataSourceIcon | undefined => {
      const icon = dataSourceIcons[dataSource as keyof typeof dataSourceIcons];
      if (!icon) {
        return !ignoreUnknown
          ? (dataSourceIcons.UNKNOWN?.[currentTheme as ThemeType]
              ?.S as string) || (dataSourceIcons.UNKNOWN?.LIGHT?.S as string)
          : undefined;
      }

      const themedIcon = icon[currentTheme as ThemeType] || icon.LIGHT;

      return !!size ? themedIcon?.[size] : themedIcon;
    },
    [currentTheme]
  );

  const getSvg = useCallback(
    (iconName: string): string | undefined => {
      const icon = DefaultAppIcons[iconName as keyof typeof DefaultAppIcons];

      if (!icon) {
        return undefined;
      }

      return icon[currentTheme as ThemeType] || icon.LIGHT;
    },
    [currentTheme]
  );

  return { getProviderIcon, getDataSourceIcon, getSvg };
};
